import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'

interface NavDropdownItem {
  to: string
  label: string
  testId?: string
  end?: boolean
}

interface NavDropdownProps {
  to: string
  label: string
  testId: string
  items: NavDropdownItem[]
}

const buildParentClassName =
  (forceActive: boolean) =>
  ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1 text-sm transition-colors ${isActive || forceActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground'}`

const itemClassName = ({ isActive }: { isActive: boolean }) =>
  `block px-4 py-2 text-sm transition-colors ${isActive ? 'font-semibold text-foreground' : 'font-medium text-muted-foreground hover:text-foreground hover:bg-muted'}`

export const NavDropdown = ({ to, label, testId, items }: NavDropdownProps) => {
  const { pathname } = useLocation()
  // parent should look active whenever any of its children matches the current path,
  // not only when the parent's own `to` exactly matches.
  const childMatches = items.some((i) => pathname === i.to || pathname.startsWith(`${i.to}/`))

  return (
    <div className="relative group h-full flex items-center">
      <NavLink to={to} data-testid={testId} className={buildParentClassName(childMatches)}>
        {label}
        <ChevronDown className="h-3.5 w-3.5 transition-transform duration-200 group-hover:rotate-180" />
      </NavLink>
      <div className="absolute top-full left-0 hidden group-hover:block z-50">
        <div className="bg-popover border border-border rounded-md shadow-md py-1 min-w-[130px]">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testId}
              className={itemClassName}
            >
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  )
}
